import React, { useState, useEffect } from 'react';
import { getAllTeams, updateTeamStatus, deleteTeam } from '../utils/teams';
import AdminLayout from '../layouts/AdminLayout';
import { FiSearch, FiX, FiCheck, FiTrash2, FiUsers, FiLayers, FiEye, FiMail, FiUser, FiAlertTriangle } from 'react-icons/fi';

const Teams = () => {
  const [teams, setTeams] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: ''
  });
  const [message, setMessage] = useState({});
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);

  useEffect(() => {
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const filtered = teams.filter(team =>
      team.team_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      team.leader?.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredTeams(filtered);
  }, [searchTerm, teams]);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      setError('');

      const result = await getAllTeams(filters);
      if (result.success) {
        setTeams(result.data);
        setFilteredTeams(result.data);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
      setError('حدث خطأ في جلب بيانات الفرق');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (teamId, newStatus) => {
    try {
      const result = await updateTeamStatus(teamId, newStatus);
      setMessage({ type: result.success ? 'success' : 'error', text: result.message });
      
      if (result.success) {
        setTeams(prev =>
          prev.map(team =>
            team.id === teamId
              ? { ...team, status: newStatus }
              : team
          )
        );
      }
      setTimeout(() => setMessage({}), 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setMessage({ type: 'error', text: 'حدث خطأ أثناء تحديث الحالة' });
    }
  };

  const handleDeleteTeam = async (teamId) => {
    try {
      const result = await deleteTeam(teamId);
      setMessage({ type: result.success ? 'success' : 'error', text: result.message });
      
      if (result.success) {
        setTeams(prev => prev.filter(team => team.id !== teamId));
      }
      setShowDeleteModal(null);
      setTimeout(() => setMessage({}), 3000);
    } catch (err) {
      console.error('Error deleting team:', err);
      setMessage({ type: 'error', text: 'حدث خطأ أثناء حذف الفريق' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'مقبول':
        return { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500' };
      case 'مرفوض':
        return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500' };
      default:
        return { bg: 'bg-yellow/20', text: 'text-yellow', border: 'border-yellow' };
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow mx-auto mb-4"></div>
            <p className="text-yellow">جاري التحميل...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-yellow mb-2">
          إدارة <span className="text-white">الفرق</span>
        </h1>
        <p className="text-white/70">مراجعة وموافقة تكوين الفرق المشاركة</p>
      </div>

      {/* Success/Error Message */}
      {message.text && (
        <div className={`mb-6 p-4 rounded-lg flex items-center justify-between border-2 ${
          message.type === 'success'
            ? 'bg-green-500/20 border-green-500 text-green-400'
            : 'bg-red-500/20 border-red-500 text-red-400'
        }`}>
          <div className="flex items-center gap-2">
            {message.type === 'success' ? <FiCheck /> : <FiX />}
            <span>{message.text}</span>
          </div>
          <button onClick={() => setMessage({})} className="text-white/70 hover:text-white">
            <FiX />
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <FiSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50" size={20} />
              <input
dir="ltr"                type="text"
                placeholder="البحث باسم الفريق أو قائد الفريق..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-3 bg-blue border-2 border-white/20 text-white placeholder-white/50 rounded-lg focus:outline-none focus:border-yellow"
              />
            </div>
          </div>

          <select
            value={filters.status}
            onChange={(e) => setFilters({ status: e.target.value })}
            className="px-4 py-3 bg-blue border-2 border-white/20 text-white rounded-lg focus:outline-none focus:border-yellow"
          >
            <option value="">جميع الحالات</option>
            <option value="قيد المراجعة">قيد المراجعة</option>
            <option value="مقبول">مقبول</option>
            <option value="مرفوض">مرفوض</option>
          </select>
        </div>

        {(filters.status || searchTerm) && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-white/70">
              عرض {filteredTeams.length} من {teams.length} فريق
            </p>
            <button
              onClick={() => {
                setFilters({ status: '' });
                setSearchTerm('');
              }}
              className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
            >
              <FiX size={16} />
              <span>مسح الفلاتر</span>
            </button>
          </div>
        )}
      </div>

      {/* Teams Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredTeams.map((team) => {
          const statusColors = getStatusColor(team.status);
          return (
            <div key={team.id} className="bg-dark-blue border-2 border-yellow rounded-2xl overflow-hidden hover:border-white/50 transition-all duration-300">
              {/* Card Header */}
              <div className="bg-yellow px-6 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-[#05339c] p-2 rounded-lg">
                      <FiLayers className="text-yellow" size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#05339c]">{team.team_name}</h3>
                      <p className="text-[#05339c]/70 text-sm mt-1">
                        {team.team_members?.length || 0} أعضاء
                      </p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold border-2 ${statusColors.bg} ${statusColors.text} ${statusColors.border}`}>
                    {team.status}
                  </span>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6 space-y-4">
                {/* Leader Info */}
                <div className="bg-blue rounded-xl p-4 border-2 border-white/10">
                  <p className="text-xs text-white/50 mb-2">قائد الفريق</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow rounded-full flex items-center justify-center text-[#05339c] font-semibold">
                      {team.leader?.full_name?.charAt(0) || 'ق'}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{team.leader?.full_name}</p>
                      <p className="text-sm text-white/60 flex items-center gap-1">
                        <FiMail size={12} />
                        {team.leader?.email}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Idea */}
                <div>
                  <p className="text-xs text-white/50 mb-2">فكرة المشروع</p>
                  <p className="text-sm text-white/80 line-clamp-3">{team.idea}</p>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-white/10">
                  <button
                    onClick={() => setSelectedTeam(team)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue text-yellow border-2 border-yellow rounded-lg hover:bg-blue/80 transition-colors text-sm font-medium"
                  >
                    <FiEye size={16} />
                    <span>عرض التفاصيل</span>
                  </button>

                  {team.status !== 'مقبول' && (
                    <button
                      onClick={() => handleStatusUpdate(team.id, 'مقبول')}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 border-2 border-green-500 rounded-lg hover:bg-green-500/30 transition-colors text-sm font-medium"
                    >
                      <FiCheck size={16} />
                      <span>قبول</span>
                    </button>
                  )}

                  {team.status !== 'مرفوض' && (
                    <button
                      onClick={() => handleStatusUpdate(team.id, 'مرفوض')}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 border-2 border-red-500 rounded-lg hover:bg-red-500/30 transition-colors text-sm font-medium"
                    >
                      <FiX size={16} />
                      <span>رفض</span>
                    </button>
                  )}

                  <button
                    onClick={() => setShowDeleteModal(team)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 border-2 border-red-500 rounded-lg hover:bg-red-500/30 transition-colors text-sm font-medium mr-auto"
                  >
                    <FiTrash2 size={16} />
                    <span>حذف</span>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredTeams.length === 0 && (
        <div className="bg-dark-blue border-2 border-yellow rounded-2xl p-12 text-center">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiSearch className="text-white/50" size={32} />
          </div>
          <p className="text-white mb-2">لا توجد فرق مطابقة</p>
          <p className="text-white/60 text-sm">جرب تغيير الفلاتر أو البحث</p>
        </div>
      )}

      {/* Team Details Modal */}
      {selectedTeam && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-yellow px-6 py-4 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-[#05339c]">{selectedTeam.team_name}</h3>
                <p className="text-[#05339c]/70 text-sm">تفاصيل الفريق</p>
              </div>
              <button
                onClick={() => setSelectedTeam(null)}
                className="text-[#05339c] hover:bg-[#05339c] hover:text-yellow p-2 rounded-lg transition-all"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status */}
              <div className="bg-blue rounded-xl p-4 border-2 border-white/10">
                <p className="text-xs text-white/50 mb-2">الحالة</p>
                <span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold border-2 ${getStatusColor(selectedTeam.status).bg} ${getStatusColor(selectedTeam.status).text} ${getStatusColor(selectedTeam.status).border}`}>
                  {selectedTeam.status}
                </span>
              </div>

              {/* Leader */}
              <div>
                <h4 className="font-semibold text-yellow mb-3 flex items-center gap-2">
                  <FiUser />
                  قائد الفريق
                </h4>
                <div className="bg-blue rounded-xl p-4 flex items-center gap-4 border-2 border-white/10">
                  <div className="w-12 h-12 bg-yellow rounded-full flex items-center justify-center text-[#05339c] font-bold text-lg">
                    {selectedTeam.leader?.full_name?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-semibold text-white">{selectedTeam.leader?.full_name}</p>
                    <p className="text-sm text-white/60">{selectedTeam.leader?.email}</p>
                  </div>
                </div>
              </div>

              {/* Members */}
              <div>
                <h4 className="font-semibold text-yellow mb-3 flex items-center gap-2">
                  <FiUsers />
                  أعضاء الفريق ({selectedTeam.team_members?.length || 0})
                </h4>
                <div className="space-y-3">
                  {selectedTeam.team_members?.map((member, index) => (
                    <div key={index} className="bg-blue rounded-xl p-4 flex items-center gap-4 border-2 border-white/10">
                      <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-white font-semibold">
                        {member.applicant?.full_name?.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-white">{member.applicant?.full_name}</p>
                        <p className="text-sm text-white/60">{member.applicant?.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Idea */}
              <div>
                <h4 className="font-semibold text-yellow mb-3">فكرة المشروع</h4>
                <div className="bg-blue rounded-xl p-4 border-2 border-white/10">
                  <p className="text-white/80 leading-relaxed">{selectedTeam.idea}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-white/10">
                {selectedTeam.status !== 'مقبول' && (
                  <button
                    onClick={() => {
                      handleStatusUpdate(selectedTeam.id, 'مقبول');
                      setSelectedTeam(null);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 transition-colors font-semibold"
                  >
                    <FiCheck size={20} />
                    <span>قبول الفريق</span>
                  </button>
                )}
                {selectedTeam.status !== 'مرفوض' && (
                  <button
                    onClick={() => {
                      handleStatusUpdate(selectedTeam.id, 'مرفوض');
                      setSelectedTeam(null);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-semibold"
                  >
                    <FiX size={20} />
                    <span>رفض الفريق</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-blue border-2 border-yellow rounded-2xl max-w-md w-full">
            <div className="p-6">
              <div className="w-12 h-12 bg-red-500/20 border-2 border-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiAlertTriangle className="text-red-400" size={24} />
              </div>
              <h3 className="text-xl font-bold text-yellow text-center mb-2">
                تأكيد الحذف
              </h3>
              <p className="text-white/70 text-center mb-6">
                هل أنت متأكد من حذف فريق <strong className="text-white">{showDeleteModal.team_name}</strong>؟<br />
                لا يمكن التراجع عن هذا الإجراء.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteModal(null)}
                  className="flex-1 px-4 py-3 bg-blue text-white border-2 border-white/20 rounded-xl hover:bg-blue/80 transition-colors font-semibold"
                >
                  إلغاء
                </button>
                <button
                  onClick={() => handleDeleteTeam(showDeleteModal.id)}
                  className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors font-semibold"
                >
                  حذف
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default Teams;
